import os from 'node:os';
import path from 'node:path';

/**
 * Encode an absolute cwd the way Claude Code does to build the per-project
 * directory name under `~/.claude/projects/`. Claude Code replaces any
 * character outside `[A-Za-z0-9_-]` with `-`, which means BOTH path
 * separators and dots collapse to dashes — consecutive specials produce
 * consecutive dashes.
 *
 * Examples (verified against `~/.claude/projects/` entries on macOS):
 *   '/Users/q/repos/foo'              -> '-Users-q-repos-foo'
 *   '/Users/q/.config/amp'            -> '-Users-q--config-amp'   (note the double dash)
 *   '/Users/q/repos/lore/.claude/wt'  -> '-Users-q-repos-lore--claude-wt'
 *
 * This is the raw one-for-one transform. Callers that receive un-normalized
 * input (e.g. the CLI reading a `project` field from history entries) should
 * normalize/validate before calling — see `apps/cli`'s `claudeProjectDirName`.
 */
export function encodeCwdToDir(cwd: string): string {
  return cwd.replace(/[^A-Za-z0-9_-]/g, '-');
}

/**
 * Claude Code projects root on macOS/Linux.
 *
 * Claude Code stores its config tree (including `projects/`) under
 * `CLAUDE_CONFIG_DIR` when that env var is set, falling back to `~/.claude`.
 * Honoring it is required wherever Claude Code runs with a relocated config
 * dir — most notably Cowork, which points it at a per-session
 * `local_<id>/.claude` directory, but also any power user who sets it. Without
 * this, `findById`/`listSessions` look under `~/.claude/projects` and miss the
 * transcript entirely.
 *
 * Both overrides accept an explicit value for tests: `home` overrides
 * `os.homedir()`, and `configDir` overrides `process.env.CLAUDE_CONFIG_DIR`.
 * A set `configDir` wins over `home`.
 */
export function claudeProjectsRoot(
  home: string = os.homedir(),
  configDir: string | undefined = process.env.CLAUDE_CONFIG_DIR,
): string {
  const overridden = configDir?.trim();
  if (overridden) return path.join(overridden, 'projects');
  return path.join(home, '.claude', 'projects');
}
